<template>
	<div class="my-4">
		<p class="flex items-center gap-1 text-lg font-semibold italic">
			{{ t('document.offlineFormPrompt') }}
			<button
				:aria-label="t('document.offlineFormToggleLabel')"
				type="button"
				class="flex gap-1"
				@click="toggleForm"
			>
				<span class="text-primary-500 italic underline">
					{{ t('document.offlineFormStepLink') }}
				</span>
				<ChevronRightIcon
					class="text-primary-500 h-6 w-6 text-xs"
					stroke-width="2"
					:class="{ 'rotate-90 transform': showForm }"
				/>
			</button>
		</p>
		<div
			v-if="showForm"
			class="flex flex-col gap-8 pb-8"
			:aria-label="t('document.offlineFormContainerLabel')"
		>
			<div class="mt-16 flex gap-[8%]">
				<div
					class="relative grid flex-1 grid-rows-[1fr_1.5fr_1fr] place-content-center items-center gap-6 rounded-lg border border-slate-100 p-10 text-center shadow-lg"
				>
					<div
						class="bg-primary-100 font-sansCondensed text-primary-600 absolute -top-5 left-1/2 w-1/4 -translate-x-1/2 rounded-full py-2 text-center text-xl font-bold uppercase"
					>
						{{ t('document.step') }} 1
					</div>
					<img
						class="mx-auto -mb-5"
						:src="PhoneCallImage"
					/>
					<div class="flex flex-col items-center gap-2">
						<p class="text-primary-600 font-serif text-2xl font-bold">
							{{ t('document.secureLine') }}
						</p>
						<span
							class="text-secondary-600 px-[20%] font-sans text-lg font-extralight"
						>
							{{ t('document.secureLineInstructions') }}
						</span>
					</div>

					<div
						class="text-primary-600 mx-auto mt-1 w-fit rounded-lg border border-slate-50 px-3 py-2 text-xl font-semibold tracking-widest shadow-sm"
					>
						800-645-0006
					</div>
				</div>
				<div
					class="relative grid flex-1 grid-rows-[1fr_1.5fr_1fr] place-content-center items-center gap-6 rounded-lg border border-slate-100 p-10 text-center shadow-lg"
				>
					<div
						class="bg-primary-100 font-sansCondensed text-primary-600 absolute -top-5 left-1/2 w-1/4 -translate-x-1/2 rounded-full py-2 text-center text-xl font-bold uppercase"
					>
						{{ t('document.step') }} 2
					</div>
					<div
						class="mx-auto -mb-5 w-fit rounded-md border-[3px] border-black text-black"
					>
						<p
							class="mx-1 -mb-2 pr-0.25 text-center text-2xl font-bold tracking-tight"
						>
							****
						</p>
					</div>
					<div class="flex flex-col items-center gap-2">
						<p class="text-primary-600 font-serif text-2xl font-bold">
							{{ t('document.enterCode') }}
						</p>
						<span
							class="text-secondary-600 px-[20%] font-sans text-lg font-extralight"
						>
							{{ t('document.enterCodeInstructions') }}
						</span>
					</div>
					<FormKit
						type="text"
						name="token"
						:label="t('document.token')"
						outer-class="max-w-full! px-4"
						input-class="h-8"
						inner-class="border! border-slate-300! rounded-lg"
						validation="required"
						v-model="token"
					/>
				</div>
			</div>
			<Teleport
				defer
				to="#paymentNext"
			>
				<button
					id="offlineButtonNext"
					type="button"
					class="text-accent-700! h-12 w-52 justify-center self-end rounded-md border-0 font-extrabold transition-all duration-300"
					:class="
						valid
							? 'bg-accent-400! hover:bg-accent-500! hover:text-accent-800!'
							: 'bg-secondary-300! hover:bg-secondary-300! cursor-not-allowed text-slate-500/50! hover:text-slate-500/50!'
					"
					@click="handleAuthorizePayment"
				>
					{{ t('document.authorizePayment') }}
				</button>
			</Teleport>
		</div>
	</div>
</template>

<script setup lang="ts">
import PhoneCallImage from '@/assets/phone_call.svg'
import { usePresentationLocale } from '@/i18n'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { ChevronRightIcon } from '@heroicons/vue/24/outline'
import { computed, nextTick, ref } from 'vue'

const { t } = usePresentationLocale()
const showForm = ref(false)
const sessionDocumentsStore = useSessionDocumentsStore()
const token = ref('')
const valid = computed(() => token.value?.length > 0)

const handleAuthorizePayment = () => {
	if (valid.value) {
		sessionDocumentsStore.setPaymentStepTo('document')
	}
}

const toggleForm = async () => {
	showForm.value = !showForm.value
	if (showForm.value) {
		await nextTick()

		document.querySelector('#step-content-container')?.scrollTo({
			top: document.querySelector('#step-content-container')?.scrollHeight,
		})
	}
}
</script>
